import {actionTool, objectInput, readyForPerson, stringInput, waitForSecrets} from "@/functions/webMcpTool";
import {describeCurrentStep} from "@/functions/webMcpStep";
import {fillInput, findElement, press, pressButtonWithText, waitForElement} from "@/functions/webMcpDom";

export const TERMS_REASON = 'clicking it agrees to the Terms of Use and Privacy Policy, which only the person can accept. get_legal_document reads them.'
import getBrandName from "@/functions/getBrandName";

const usernameInput = () => objectInput({
	username: stringInput(`The person's ${getBrandName()} username. Leave it out if a password manager fills it.`, {maxLength: 128}),
})

const SIGN_IN_NOTE = 'Pressing Sign in agrees to the Terms of Use, so the tool stops there and returns ready_for_person; the person presses it, and the site runs its captcha. Then get_auth_state shows the next step: two-step verification, a required change, a passkey offer, or the tax return.'

const buildSignIn = () => actionTool({
	name: 'sign_in',
	description: `Get the ${getBrandName()} sign-in form ready for the person. Enters the username; the password goes into the page from the person, their password manager or your secure sign-in, never through this tool. ${SIGN_IN_NOTE}`,
	inputSchema: usernameInput(),
	run: ({username}) => {
		if (username) fillInput('#username', username, 'username field')
		return readyForPerson('form button[type="submit"]', {secrets: ['#username', '#password'], reason: TERMS_REASON})
	},
})

const buildSsoSignIn = () => actionTool({
	name: 'sign_in',
	description: `Sign the person in on the single sign-on page. Enters the username and presses Continue; if the account uses a partner provider, the browser goes there. When the site asks for a password, it goes into the page from the person or their password manager, never through this tool. ${SIGN_IN_NOTE}`,
	inputSchema: usernameInput(),
	run: async ({username}) => {
		if (!document.querySelector('#password')) {
			if (username) fillInput('#username', username, 'username field')
			const waiting = waitForSecrets('sign_in', ['#username'])
			if (waiting) return waiting
			const continued = press('form button[type="submit"]', 'Continue button')
			if (continued.invalid.length) return continued
			const passwordField = await waitForElement('#password')
			if (!passwordField) return {result: {outcome: 'continued', step: describeCurrentStep()}}
		}
		return readyForPerson('form button.sign-in[type="submit"]', {secrets: ['#password'], reason: TERMS_REASON})
	},
})

const buildPasskeySignIn = () => actionTool({
	name: 'sign_in_with_passkey',
	description: 'Start signing in with a passkey saved on this device. The browser or password manager shows its own prompt, and only the person can approve it with their fingerprint, face, or device PIN. Tell the person to approve the prompt, then call get_auth_state.',
	startsPasskeyPrompt: true,
	run: () => {
		const passkeyButton = document.querySelector('form button.btn-white')
		if (passkeyButton) {
			pressButtonWithText('Sign in with Passkey')
			return
		}
		const username = findElement('#username', 'username field')
		username.focus()
		return {result: {outcome: 'waiting_for_person', waitingFor: 'passkey', reason: 'The username field is focused. Saved passkeys appear in the browser\'s autofill list for the person to choose; only the person can approve one.', step: describeCurrentStep()}}
	},
})

const buildChoosePartnerOption = () => actionTool({
	name: 'choose_partner_link_option',
	description: `After signing in with a partner provider, choose whether to link the partner to an existing ${getBrandName()} account or create a new ${getBrandName()} account. Linking goes to the sign in page, where the person signs in to the account to link; creating goes to the create-account page. Returns that next step.`,
	inputSchema: objectInput({option: stringInput('What the person wants to do.', {enum: ['link_existing_account', 'create_new_account']})}, ['option']),
	run: ({option}) => press(option === 'create_new_account' ? '#createNew' : '#linkExisting', option === 'create_new_account' ? 'Create a new account button' : 'Link existing account button'),
})

const buildContinueAfterPartnerLink = () => actionTool({
	name: 'continue_after_partner_link',
	description: 'Continue after the partner account link attempt, when the page shows a Continue button because the link could not be completed. The message on the page says why; read it with get_auth_state first. The browser then moves on to the tax return without the partner link.',
	run: () => press('button.sign-in', 'Continue button'),
})

const signIn = [buildSignIn, buildPasskeySignIn]

export const signInTools = {
	Login: signIn,
	LoginNew: signIn,
	PreparerLogin: signIn,
	LoginSso: [buildSsoSignIn, buildPasskeySignIn],
	PartnerTransfer: [buildChoosePartnerOption],
	PartnerLinkComplete: [buildContinueAfterPartnerLink],
}